import { Params } from '@angular/router';
import { MenuId, NavContext, NavItem, ProductMenu, ProductMenus } from './nav-config';

// Every gate declared on the item is ANDed; an absent gate is not a constraint.
export function isItemVisible(item: NavItem, ctx: NavContext): boolean {
  let visible = item.visible !== false;

  if (visible && item.requiresUser && !ctx.hasUser) {
    visible = false;
  }

  if (visible && item.requiresAccount && !ctx.hasAccount) {
    visible = false;
  }

  if (visible && item.adminOnly && !ctx.isStaff) {
    visible = false;
  }

  if (visible && item.showWhen) {
    visible = item.showWhen(ctx);
  }

  return visible;
}

// Prunes parents left with no reachable children, unless they have a target of their own.
export function filterNavItems(items: NavItem[], ctx: NavContext): NavItem[] {
  const result: NavItem[] = [];

  items.forEach((item: NavItem) => {
    if (isItemVisible(item, ctx)) {
      const copy: NavItem = Object.assign({}, item);

      if (item.children && item.children.length) {
        copy.children = filterNavItems(item.children, ctx);
      }

      let keep = true;
      const declaresChildren = !!(item.children && item.children.length);
      const hasVisibleChildren = !!(copy.children && copy.children.length);
      const hasOwnTarget = !!(item.route || item.dynamicRoute || item.opensLegacy || item.dynamicChildrenKey);

      if (declaresChildren && !hasVisibleChildren && !hasOwnTarget) {
        keep = false;
      }

      // A coming-soon row leads nowhere by definition, so it is not pruned as a dead end.
      if (item.comingSoon) {
        keep = true;
      }

      if (keep) {
        result.push(copy);
      }
    }
  });

  return result;
}

export function resolveLabel(item: NavItem, ctx: NavContext): string {
  let label = item.label;

  if (item.dynamicLabel) {
    label = item.dynamicLabel(ctx);
  }

  return label;
}

export function resolveRoute(item: NavItem, ctx: NavContext): any[] {
  let route = item.route;

  if (item.dynamicRoute) {
    route = item.dynamicRoute(ctx);
  }

  if (route && item.routeParams) {
    route = route.concat([item.routeParams]);
  }

  return route;
}

export function getProductMenu(id: MenuId): ProductMenu {
  const menu = ProductMenus.find((candidate: ProductMenu) => candidate.id === id);

  return menu || null;
}

export function routePath(route: any[]): string {
  return route.join('/').replace(/\/+/g, '/');
}

function menuRows(menu: ProductMenu): NavItem[] {
  let rows: NavItem[] = [];

  menu.items.forEach((item: NavItem) => {
    rows = rows.concat([item], item.children || []);
  });

  return rows;
}

export function urlIsUnder(url: string, path: string): boolean {
  return url === path || url.indexOf(path + '/') === 0;
}

function rowQueryMatches(row: NavItem, queryParams: Params): boolean {
  let matches = false;

  if (row.queryParams) {
    matches = Object.keys(row.queryParams).every((key: string) => String(queryParams[key]) === String(row.queryParams[key]));
  }

  return matches;
}

// Menus can share a route (Merchant Profiles), so the open menu keeps a url it lists and the pages under it.
export function menuClaimsUrl(id: MenuId, url: string): boolean {
  const menu = getProductMenu(id);
  let claims = false;

  if (menu) {
    claims = menuRows(menu).some((row: NavItem) => !!row.route && urlIsUnder(url, routePath(row.route)));
  }

  return claims;
}

export function menuForUrl(url: string, queryParams: Params = {}): MenuId {
  let matched: MenuId = MenuId.Home;
  let matchedLength = -1;

  ProductMenus.forEach((menu: ProductMenu) => {
    menuRows(menu).forEach((row: NavItem) => {
      // An exact route is shared, so it cannot say which menu the url belongs to.
      if (row.route && row.exact !== true) {
        const path = routePath(row.route);
        // On a shared route (Merchant Profiles) the row whose query params the url carries picks the menu.
        const wins = path.length > matchedLength || (path.length === matchedLength && rowQueryMatches(row, queryParams));

        if (urlIsUnder(url, path) && wins) {
          matched = menu.id;
          matchedLength = path.length;
        }
      }
    });
  });

  return matched;
}
